export type HomeTileType = "QCOMM" | "SERVICE";

export type HomeTileConfigStatus = "ACTIVE" | "INACTIVE";

export type HomeTileConfigWireStatus = "active" | "inactive" | "deleted";

export type RibbonResolutionOrder = "DYNAMIC_STATIC" | "STATIC_DYNAMIC";

export type MembershipRibbonFormat = "FREE" | "SERVICES_REMAINING" | "SERVICES_USED";

export type DynamicRibbonFormat = "discount" | "usage_fraction" | "usage_remaining";

export const RIBBON_POSITIONS = [
  "top_left",
  "top_center",
  "top_right",
  "bottom_left",
  "bottom_center",
  "bottom_right",
  "top_full_width",
  "full_width",
] as const;

export type RibbonPosition = (typeof RIBBON_POSITIONS)[number];

export interface IRibbonStyle {
  bg_color: string;
  text_color: string;
  border_color: string;
  position: RibbonPosition;
}

export type IRibbonColors = Pick<IRibbonStyle, "bg_color" | "text_color" | "border_color">;

export const DEFAULT_RIBBON_STYLE: IRibbonStyle = {
  bg_color: "#DCFCE7",
  text_color: "#166534",
  border_color: "#86EFAC",
  position: "top_center",
};

export type IPersonaRuleNode =
  { persona: string; value: boolean } | { operator: PersonaRuleOperator; conditions: IPersonaRuleNode[] };

export type PersonaRuleOperator = "AND" | "OR";

export const isPersonaRuleGroup = (
  node: IPersonaRuleNode,
): node is { operator: PersonaRuleOperator; conditions: IPersonaRuleNode[] } => "operator" in node;

export interface IHomeTileConfiguration {
  persona_rule?: IPersonaRuleNode | null;
  is_dynamic_enabled?: boolean;
  membership_ribbon_format?: MembershipRibbonFormat | null;
  resolution_order?: RibbonResolutionOrder | null;
}

export interface IHomeTileApiRow {
  id: string;
  tile_name: string;
  display_name: string;
  tile_type: string;
  tile_image: string | null;
  visible: boolean;
  web_visible: boolean;
  active_config_count?: number;
  is_dynamic_enabled?: boolean;
  dynamic_ribbon_member_format?: DynamicRibbonFormat | null;
  dynamic_ribbon?: IRibbonStyle | null;
  updated_at?: number | null;
}

export interface IHomeTileConfigApiRow {
  config_id?: string;
  id?: string;
  tile_id: string;
  configuration: IHomeTileConfiguration | null;
  ribbon_text: string;
  ribbon: IRibbonStyle | null;
  status: string;
  overrides_dynamic?: boolean | null;
  created_at?: number | null;
  updated_at?: number | null;
  updated_by?: string | null;
}

export interface IHomeTileDetailApiRow {
  tile?: IHomeTileApiRow | null;
  configurations?: IHomeTileConfigApiRow[] | null;
  configs?: IHomeTileConfigApiRow[] | null;
  count?: number;
}

export interface IHomeTileOption {
  tile_id: string;
  tile_name: string;
  tile_type: HomeTileType;
  image_url?: string;
  visible: boolean;
  web_visible: boolean;
  is_dynamic_enabled: boolean;
  dynamic_ribbon_member_format: DynamicRibbonFormat | null;
  dynamic_ribbon: IRibbonStyle | null;
  active_rule_count: number;
  updated_at: number | null;
}

export interface IHomeTileConfig {
  id: string;
  tile_id: string;
  status: HomeTileConfigStatus;
  ribbon_text: string;
  ribbon: IRibbonStyle;
  persona_rule: IPersonaRuleNode | null;
  overrides_dynamic: boolean;
  is_dynamic_enabled: boolean;
  membership_ribbon_format: MembershipRibbonFormat | null;
  resolution_order: RibbonResolutionOrder | null;
  created_at: number | null;
  updated_at: number | null;
  updated_by: string | null;
}

export interface IHomeTileConfigSaveBody {
  tile_id: string;
  configuration: IHomeTileConfiguration;
  ribbon_text: string;
  ribbon: IRibbonStyle;
  status: HomeTileConfigWireStatus;
  overrides_dynamic?: boolean;
}

export type IHomeTileConfigUpdateBody = Partial<IHomeTileConfigSaveBody>;

interface IApiEnvelope<TData> {
  status: boolean;
  message: string;
  data: TData;
}

export interface IHomeTileDynamicStatusBody {
  is_dynamic_enabled: boolean;
  dynamic_ribbon_member_format?: DynamicRibbonFormat;
  dynamic_ribbon?: IRibbonStyle;
}

export type IHomeTileDynamicStatusResponse = IApiEnvelope<{ id: string; is_dynamic_enabled: boolean } | null>;

export type IHomeTileListResponse = IApiEnvelope<{ tiles: IHomeTileApiRow[]; count: number }>;
export type IHomeTileDetailResponse = IApiEnvelope<IHomeTileDetailApiRow>;
export type IHomeTileConfigMutationResponse = IApiEnvelope<IHomeTileConfigApiRow | null>;
